import React, { useState, useEffect, MouseEvent, useMemo } from 'react';
import Table from 'components/commercetools-ui/atoms/table';
import Typography from 'components/commercetools-ui/atoms/typography';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import { useRouter, useParams } from 'next/navigation';
import { tablet } from 'helpers/utils/screensizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import Icon from 'components/icons/x-mark';
import Popup from 'components/commercetools-ui/atoms/popup';
import Radio from 'components/commercetools-ui/atoms/radio';
import Spinner from 'components/commercetools-ui/organisms/spinner';
import { projectObj } from 'frontastic/tastics/project-builder-page/components/data';

interface Project {
  projectLocation: string;
  lastModifiedAt: string | undefined;
  createdAt: string | undefined;
  projectType: string | undefined;
  projectName: string | undefined;
  projectId: string;
  name: string;
  status: 'open' | 'closed';
  username: string;
  shoppingListId: string;
}

interface ProjectCardProps {
  project: Project;
  isEvenRow: boolean;
}

const tableHeader = [
  {
    label: 'Title',
    sort: true,
    valueAsc: 'nameAsc',
    valueDesc: 'nameDesc',
  },
  {
    label: 'Project Type',
    sort: true,
    valueAsc: 'projectTypeAsc',
    valueDesc: 'projectTypeDesc',
  },
  {
    label: 'Location',
    sort: true,
    valueAsc: 'locationAsc',
    valueDesc: 'locationDesc',
  },
  {
    label: 'Date Created',
    sort: true,
    valueAsc: 'createdAtAsc',
    valueDesc: 'createdAtDesc',
  },
  {
    label: 'Date Modified',
    sort: true,
    valueAsc: 'lastModifiedAtAsc',
    valueDesc: 'lastModifiedAtDesc',
  },
];

const ProjectHeadingSection = () => {
  const { locale } = useParams();

  const handleButtonClick = () => {
    window.location.href = `/${locale}/project-builder/create`;
  };

  return (
    <div className="mb-[30px] flex w-full flex-col text-3xl lg:mb-40 lg:flex-row lg:justify-between">
      <span className="mb-[30px] lg:mb-0">My Projects</span>
      <Button variant={'secondary'} className="!border-[#dee2e6] cursor-pointer w-full lg:w-auto" type="button" onClick={handleButtonClick}>
        Add new project
      </Button>
    </div>
  );
};

const formatDate = (date: string | undefined) => {
  return date
    ? new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : 'N/A';
};

const optionsData = [
  { name: 'Title A to Z', checked: false, value: 'nameAsc' },
  { name: 'Title Z to A', checked: false, value: 'nameDesc' },
  { name: 'Project Type A to Z', checked: false, value: 'projectAsc' },
  { name: 'Project Type Z to A', checked: false, value: 'projectDesc' },
  { name: 'Location A to Z', checked: false, value: 'locationAsc' },
  { name: 'Location Z to A', checked: false, value: 'locationDesc' },
  { name: 'CreatedAt Asc', checked: false, value: 'createdAtAsc' },
  { name: 'CreatedAt Desc', checked: false, value: 'createdAtDesc' },
  { name: 'ModifiedAt Asc', checked: false, value: 'lastModifiedAtAsc' },
  { name: 'ModifiedAt Desc', checked: false, value: 'lastModifiedAtDesc' },
];
const pageBtnClasses = {
  base: 'border px-8 py-8 text-sm font-medium text-gray-500',
  main: 'cursor-not-allowed bg-gray-300 text-gray-500',
  fallback: 'bg-white text-gray-500 hover:bg-gray-100',
};

enum TAB_STATES {
  open = 'open',
  close = 'closed',
}
interface ProjectList {
  count: number;
  limit: number;
  offset: number;
  total: number;
  results: Project[];
}
interface TotalRecords {
  totalRecords: number;
}
interface ConfirmationPopupProps {
  isOpen: boolean;
  selectedProject: { id: string; projectId: string; version: number; name: string } | null;
  onClose: (response: 'success' | 'canceled') => void;
  dropDownState: string;
}

interface DeleteProjectPayload {
  id: string;
  version: number;
}

interface UpdateProjectStatusPayload {
  shoppingListId: string;
  version: number;
  status: string;
  fieldName: string;
}

interface ProjectListProps {
  totalRecords: number;
  getMyProjectsDashboard: (payload: { limit: number; sortKey?: string; status: string; offset: number }) => void;
  deleteProject: (payload: DeleteProjectPayload) => void;
  updateProjectStatus: (payload: UpdateProjectStatusPayload) => void;
}

const ProjectList: React.FC<ProjectListProps> = ({
  totalRecords,
  getMyProjectsDashboard,
  deleteProject,
  updateProjectStatus,
}) => {
  const [activeTab, setActiveTab] = useState<TAB_STATES>(TAB_STATES.open);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const [projects, setProjects] = useState<ProjectList>({
    count: 0,
    limit: 0,
    offset: 0,
    total: totalRecords,
    results: [],
  });
  const [loading, setLoading] = useState<any>(true);
  const [openSort, setOpenSort] = useState<any>(false);
  const [offset, setOffset] = useState(0);
  const [highlightedSort, setHighlightedSort] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Number of items per page

  const [isTablet] = useMediaQuery(tablet);
  const [sortState, setSortState] = useState<{
    column: number | null;
    direction: 'up' | 'down' | null;
  }>({
    column: null,
    direction: null,
  });
  const [options, setOptions] = useState(optionsData);
  const [sortingKey, setSortingKey] = useState('');

  const router = useRouter();

  const fetchProjects = useMemo(
    () => async () => {
      setLoading(true);
      try {
        const payload: {
          limit: number;
          sortKey?: string;
          status: string;
          offset: number;
        } = { limit: itemsPerPage, status: 'open', offset: currentPage == 1 ? 0 : offset };
        if (sortingKey) {
          payload.sortKey = sortingKey;
        }
        if (!highlightedSort) {
          payload.sortKey = 'createdAtDesc';
        }
        payload.status = activeTab === TAB_STATES.open ? 'open' : 'closed';
        const responseBody: any = await getMyProjectsDashboard(payload);
        setProjects(responseBody.data);
        setOpenSort(false);
      } catch (err) {
        console.error('Error fetching projects:', err);
      } finally {
        setLoading(false);
      }
    },
    [sortingKey, activeTab, currentPage, highlightedSort],
  );

  useEffect(() => {
    fetchProjects();
  }, [activeTab, sortingKey, currentPage]);

  const totalPages = Math.ceil(projects.total / itemsPerPage);

  const handlePageChange = (newPage: number) => {
    const offsetValue = (newPage - 1) * itemsPerPage;
    setCurrentPage(newPage);
    setOffset(offsetValue);
    const element = document.getElementById('list-container');
    if (element) {
      window.scrollTo({
        top: element.offsetTop,
        behavior: 'smooth',
      });
    }
  };

  const toggleDropdown = (projectId: string) => {
    setOpenDropdown((prev) => (prev === projectId ? null : projectId));
  };

  const closeDropdown = (event: MouseEvent) => {
    if (event.target instanceof HTMLElement) {
      if (!event.target.closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
    }
  };
  useEffect(() => {
    const handleClick = (event: globalThis.MouseEvent) => {
      const target = event.target as HTMLElement;
      const isInsideDropdown = target.closest('.dropdown-container');
      const isInsidePopup = target.closest('.popup-container');

      if (!isInsideDropdown && !isInsidePopup) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('click', handleClick);
    }

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [openDropdown]);

  const ProjectDetail: React.FC<{ label?: string; value?: string }> = ({ label, value }) => {
    return (
      <div>
        {label && <Typography className="text-12">{label}</Typography>}
        {value && (
          <Typography className="whitespace-normal text-16 !font-medium leading-6 text-[#090909]">
            {value || 'NA'}
          </Typography>
        )}
      </div>
    );
  };

  const ProjectCard: React.FC<ProjectCardProps> = ({ project, isEvenRow }) => {
    return (
      <div key={project.projectId} className={`p-4 ${isEvenRow ? 'bg-[#DDDDDD]' : ''}`}>
        <div>
          <div className="projectName mb-[18px] flex items-baseline justify-between">
            <div className="break-words">
              <Typography className="text-12">Title</Typography>
              <Link variant="primary" link={`view?projectId=${project?.shoppingListId}`}>
                <Typography className="decoration-3 whitespace-normal text-16 !font-medium leading-6 text-[#090909] underline">
                  {project.projectName}
                </Typography>
              </Link>
            </div>
            <div>
              <DropDown project={project} />
            </div>
          </div>
          <ProjectDetail label="Project Type" value={projectObj[project?.projectType ?? ''] || 'NA'} />
          <ProjectDetail label="Location" value={project.projectLocation || 'NA'} />
          <ProjectDetail label="Date Created" value={formatDate(project.createdAt)} />
          <ProjectDetail label="Date Modified" value={formatDate(project.lastModifiedAt)} />
        </div>
      </div>
    );
  };

  const ProjectListRecodrs: React.FC = () => {
    return (
      <div className="mx-auto max-w-4xl">
        {projects.results?.map((project, index) => (
          <ProjectCard key={project.projectId} project={project} isEvenRow={index % 2 !== 0} />
        ))}
      </div>
    );
  };

  const ConfirmationPopup: React.FC<ConfirmationPopupProps> = ({ isOpen, selectedProject, onClose, dropDownState }) => {
    const [localShowSuccess, setLocalShowSuccess] = useState<string>('');

    if (!isOpen || !selectedProject) return null;

    const handleDelete = async () => {
      try {
        let label = '';
        if (dropDownState == 'delete') {
          const payload = {
            id: selectedProject.id,
            version: selectedProject.version,
          };
          label = 'deleted';
          await deleteProject(payload);
        } else {
          const payload = {
            shoppingListId: selectedProject.id,
            version: selectedProject.version,
            status: dropDownState,
            fieldName: 'status',
          };
          label = 'closed';
          if (activeTab == 'closed') {
            label = 'opend';
          }
          await updateProjectStatus(payload);
        }
        setLocalShowSuccess(`Project ${label} successfully.`);
        setTimeout(() => {
          onClose('success');
        }, 3000);
      } catch (err) {
        console.error('Error-', err);
        onClose('canceled');
      }
    };

    const handleCancel = (event: React.MouseEvent) => {
      event.stopPropagation();
      onClose('canceled');
    };
    let lable = 'delete';
    if (dropDownState == 'open') {
      lable = 'open';
    } else if (dropDownState == 'closed') {
      lable = 'close';
    }
    return (
      <Popup isOpen={isOpen} bodyClassNames="sm:mx-unset max-w-[380px]">
        <div className="break-words p-[25px]">
          <p className="mt-[27px] whitespace-normal text-gray-700">
            Are you sure you want to {lable} <br /> <strong>{selectedProject.name}</strong>
          </p>
          <div className="mt-[51px] flex gap-31 space-x-4">
            <Button
              variant={'secondary'}
              className="cursor-pointer !border-[#dee2e6] !px-[27px] !py-[9px]text-sm font-medium"
              type="button"
              onClick={handleDelete}
            >
              Yes, please {lable}
            </Button>

            <Button
              variant={'secondary'}
              className="cursor-pointer !border-[#dee2e6] !px-[27px] !py-[9px]text-sm font-medium"
              type="button"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
          <div className="mt-15 text-green-700">{localShowSuccess}</div>
        </div>
      </Popup>
    );
  };

  const DropDown: React.FC<{ project: any }> = ({ project }) => {
    const [openPopup, setOpenPopup] = useState<boolean>(false);
    const [selectedProject, setSelectedProject] = useState<{
      id: string;
      projectId: string;
      version: number;
      name: string;
    } | null>(null);
    const [dropDownState, setDropDownState] = useState('');

    const openPopupDialog = (id: string, projectId: string, version: number, name: string, dropdown: string) => {
      setOpenPopup(true);
      setSelectedProject({ id, projectId, version, name });
      setDropDownState(dropdown);
    };

    const handlePopupClose = (response: 'success' | 'canceled') => {
      if (response === 'canceled') {
        setOpenPopup(false);
      } else if (response === 'success') {
        fetchProjects();
        setOpenPopup(false);
        setOpenDropdown(null);
      }
    };
    const navigateToForm = (path: string, shoppingListId: string) => {
      setOpenDropdown(null);
      router.push(`${path}/?projectId=${project?.shoppingListId}`);
    };
    return (
      <div className="relative">
        <button className="space-y-1" type="button" onClick={() => toggleDropdown(project.projectId)}>
          <div className="h-4 w-4 rounded-full bg-black cursor-pointer"></div>
          <div className="h-4 w-4 rounded-full bg-black cursor-pointer"></div>
          <div className="h-4 w-4 rounded-full bg-black cursor-pointer"></div>
        </button>
        {openDropdown === project.projectId && (
          <div className="dropdown-container absolute right-0 top-full z-10 w-[110px]   rounded border border-gray-300 bg-white shadow-lg">
            <ul>
              <li className="hover:bg-gray-100">
                <button
                  className="block px-[27px] py-[9px] text-sm text-gray-700 cursor-pointer"
                  onClick={() => navigateToForm('view', project.shoppingListId)}
                >
                  View
                </button>
              </li>
              <li className="hover:bg-gray-100">
                <button
                  className="block px-[27px] py-[9px] text-sm text-gray-700 cursor-pointer"
                  onClick={() => navigateToForm('edit', project.shoppingListId)}
                >
                  Edit
                </button>
              </li>
              <li className="hover:bg-gray-100">
                <button
                  className="block px-[27px] py-[9px] text-sm text-gray-700 cursor-pointer"
                  onClick={() =>
                    openPopupDialog(
                      project.shoppingListId,
                      project.projectId,
                      project.version,
                      project.projectName,
                      'delete',
                    )
                  }
                >
                  Delete
                </button>
              </li>

              {activeTab === 'open' && (
                <li className="hover:bg-gray-100">
                  <button
                    className="block px-[27px] py-[9px] text-sm text-gray-700 cursor-pointer"
                    onClick={() =>
                      openPopupDialog(
                        project.shoppingListId,
                        project.projectId,
                        project.version,
                        project.projectName,
                        'closed',
                      )
                    }
                  >
                    Close
                  </button>
                </li>
              )}
              {activeTab === 'closed' && (
                <li className="hover:bg-gray-100">
                  <button
                    className="block px-[27px] py-[9px] text-sm text-gray-700 cursor-pointer"
                    onClick={() =>
                      openPopupDialog(
                        project.shoppingListId,
                        project.projectId,
                        project.version,
                        project.projectName,
                        'open',
                      )
                    }
                  >
                    Open
                  </button>
                </li>
              )}
            </ul>
          </div>
        )}
        {/*Confirmation Popup */}
        <div className="popup-container">
          <ConfirmationPopup
            isOpen={openPopup}
            selectedProject={selectedProject}
            onClose={handlePopupClose}
            dropDownState={dropDownState}
          />
        </div>
      </div>
    );
  };

  const handleTabChange = (key: TAB_STATES) => {
    setActiveTab(key);
    setCurrentPage(1);
  };

  const getTabComponents = () => {
    return (
      <div className="tabs-header">
        <button
          onClick={() => handleTabChange(TAB_STATES.open)}
          className={`tab-button ${activeTab === TAB_STATES.open && 'active'}`}
        >
          Open Projects
        </button>

        <button
          onClick={() => handleTabChange(TAB_STATES.close)}
          className={`tab-button ${activeTab === TAB_STATES.close && 'active'}`}
        >
          Closed Projects
        </button>
      </div>
    );
  };

  const getPaginationComponent = () => {
    return (
      <div className="mt-4 flex flex-wrap items-center justify-center">
        {currentPage !== 1 && (
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            className={`${pageBtnClasses.base} ${
              currentPage === 1 ? pageBtnClasses.main : pageBtnClasses.fallback
            } px-18 py-[4.5px] text-[25px]`}
          >
            ‹
          </button>
        )}
        <div className="flex flex-wrap justify-center">
          {Array.from({ length: totalPages }, (_, index) => (
            <button
              className={`${pageBtnClasses.base} ${
                currentPage === index + 1 ? 'bg-gray-100 text-black' : pageBtnClasses.fallback
              } px-[18px] py-[4.5px]`}
              onClick={() => handlePageChange(index + 1)}
              key={index}
            >
              {index + 1}
            </button>
          ))}
        </div>
        {currentPage !== totalPages && (
          <button
            className={`${pageBtnClasses.base} ${
              currentPage === totalPages ? pageBtnClasses.main : pageBtnClasses.fallback
            } px-[4.5px] py-[4.5px] text-[25px]`}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            ›
          </button>
        )}
      </div>
    );
  };

  const handleSort = (index: number) => {
    const updatedOptions = options.map((option, i) => ({
      ...option,
      checked: i === index,
    }));
    setOptions(updatedOptions);
    setSortingKey(updatedOptions[index].value);
    setHighlightedSort(true);
    fetchProjects();
  };

  const handleSortForDesktop = (index: number, direction: 'up' | 'down', sortingValue?: string) => {
    if (sortState.column === index && sortState.direction === direction) {
      setSortState({
        column: null,
        direction: null,
      });
      setHighlightedSort(false);
      setSortingKey('createdAtDesc');
    } else {
      setSortState({
        column: index,
        direction,
      });
      setHighlightedSort(true);
      if (sortingValue) {
        setSortingKey(sortingValue);
      }
    }
    fetchProjects();
  };

  const handleClose = () => {
    setOpenSort(false);
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <>
      <ProjectHeadingSection />
      <div className="project-list">
        {getTabComponents()}
        {isTablet ? (
          <div>
            <Table.Container className="w-full max-w-full table-fixed">
              <Table.Head>
                <Table.Row>
                  {tableHeader.map((e, index) => (
                    <Table.Cell key={e.label}>
                      <div className="flex items-center gap-[0.5rem]">
                        <Typography className="text-12">{e.label}</Typography>
                        {e.sort && (
                          <div>
                            <span
                              className={`cursor-pointer ${
                                sortState.column === index && sortState.direction === 'up' ? 'text-black' : ''
                              }`}
                              onClick={() => handleSortForDesktop(index, 'up', e.valueAsc)}
                            >
                              ↑
                            </span>
                            <span
                              className={`cursor-pointer ${
                                sortState.column === index && sortState.direction === 'down' ? 'text-black' : ''
                              }`}
                              onClick={() => handleSortForDesktop(index, 'down', e.valueDesc)}
                            >
                              ↓
                            </span>
                          </div>
                        )}
                      </div>
                    </Table.Cell>
                  ))}
                </Table.Row>
              </Table.Head>

              <Table.Body>
                {projects.results?.map((project, index) => (
                  <Table.Row
                    key={project.projectId}
                    className={`${index % 2 === 1 ? 'bg-gray-100' : ''} mb-18px w-full`}
                  >
                    <Table.Cell className="break-words">
                      <Link variant="primary" link={`view?projectId=${project?.shoppingListId}`}>
                        <Typography className="decoration-3 whitespace-normal text-16 !font-medium leading-6 text-[#090909] underline">
                          {project?.projectName}
                        </Typography>
                      </Link>
                    </Table.Cell>
                    <Table.Cell className="w-[15%]">
                      <ProjectDetail value={projectObj[project?.projectType ?? ''] || 'NA'} />
                    </Table.Cell>
                    <Table.Cell className="w-[13%]">
                      <ProjectDetail value={project?.projectLocation || 'NA'} />
                    </Table.Cell>
                    <Table.Cell className="w-[12%]">
                      <ProjectDetail value={formatDate(project.createdAt)} />
                    </Table.Cell>
                    <Table.Cell className="relative w-[150px]">
                      <div className="flex justify-between">
                        <ProjectDetail value={formatDate(project.lastModifiedAt)} />
                        <DropDown project={project} />
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Container>
            {getPaginationComponent()}
          </div>
        ) : (
          <>
            {!isTablet && (
              <>
                <div className="mb-[24px] mt-[48px] flex w-full flex-col text-3xl lg:flex-row lg:justify-between">
                  <Button
                    variant={'secondary'}
                    className=" w-full lg:w-auto"
                    type="button"
                    onClick={() => setOpenSort(!openSort)}
                  >
                    ↑↓ Sort
                  </Button>
                </div>
                <Popup isOpen={openSort} bodyClassNames="mx-20 sm:mx-unset  max-w-[535px]">
                  <div className="mb-20 flex items-center pl-25 pt-32">
                    <Typography className="flex flex-1 justify-start !text-20 !text-[090909]">
                      Sortby following field
                    </Typography>
                    <div className="cursor-p pr-15" onClick={handleClose}>
                      <Icon />
                    </div>
                  </div>
                  <div className="pt-20">
                    {options.map((option, index) => (
                      <div
                        key={index}
                        className={`${index !== 0 ? 'mt-12' : ''} ${
                          index === options.length - 1 ? 'mb-20' : ''
                        } flex cursor-pointer items-center gap-16 pl-24`}
                        onClick={() => handleSort(index)}
                      >
                        <Radio name={option.name} checked={option.checked} />
                        <div>
                          <p className="text-14 font-medium">{option.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Popup>
              </>
            )}
            <ProjectListRecodrs />

            {getPaginationComponent()}
          </>
        )}
      </div>
    </>
  );
};

export default ProjectList;
